import { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import PageShell from '@/layout/PageShell'
import { supabase } from '@/services/supabaseClient'
import { getCollegeByAdminEmail } from '@/utils/userAccess'

export default function SetupPage() {
  const navigate = useNavigate()

  const [setupData, setSetupData] = useState({
    college_name: '',
    college_name_en: '',
    principal_name: '',
    principal_name_en: '',
    college_mobile: '',
    email: '',
    website: '',
    college_address: '',
    college_address_en: '',
    college_eiin: '',
    admin_email: '',
    college_logo_url: ''
  })

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState<{ text: string; type: 'success' | 'error' } | null>(null)

  useEffect(() => {
    loadSetup()
  }, [])

  async function loadSetup() {
    setLoading(true)
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (user?.email) {
        const col = await getCollegeByAdminEmail(user.email)
        if (col) {
          setSetupData({
            college_name: col.college_name || '',
            college_name_en: col.college_name_en || '',
            principal_name: col.principal_name || '',
            principal_name_en: col.principal_name_en || '',
            college_mobile: col.college_mobile || '',
            email: col.email || '',
            website: col.website || '',
            college_address: col.college_address || '',
            college_address_en: col.college_address_en || '',
            college_eiin: String(col.college_eiin || ''),
            admin_email: col.admin_email || user.email || '',
            college_logo_url: col.college_logo_url || ''
          })
        }
      }
    } catch (err: any) {
      console.error('Failed to load college setup:', err)
    } finally {
      setLoading(false)
    }
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault()
    const { data: { user } } = await supabase.auth.getUser()
    if (!user?.email) return

    setSaving(true)
    setMsg(null)

    try {
      const { error } = await supabase
        .from('college_user')
        .update({
          college_name: setupData.college_name.trim(),
          college_address: setupData.college_address.trim(),
          college_mobile: setupData.college_mobile.trim(),
          website: setupData.website.trim(),
          email: setupData.email.trim(),
          principal_name: setupData.principal_name.trim(),
          college_name_en: setupData.college_name_en.trim(),
          college_address_en: setupData.college_address_en.trim(),
          principal_name_en: setupData.principal_name_en.trim()
        })
        .ilike('admin_email', user.email.trim().toLowerCase())

      if (error) throw error

      setMsg({ text: '⚙️ Settings updated successfully!', type: 'success' })
    } catch (err: any) {
      setMsg({ text: err.message || 'Failed to update settings', type: 'error' })
    } finally {
      setSaving(false)
    }
  }

  return (
    <PageShell title="⚙️ System Setup & Settings" backHref="/dashboard">
      {(_user) => (
        <div style={{ maxWidth: '960px', margin: '0 auto', padding: '24px 16px' }}>
          
          {/* Header Banner */}
          <div style={{
            background: 'linear-gradient(135deg, #0f172a, #1e293b)',
            color: '#fff',
            borderRadius: '20px',
            padding: '28px 32px',
            marginBottom: '28px',
            boxShadow: '0 10px 25px rgba(15, 23, 42, 0.25)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '16px'
          }}>
            <div>
              <h2 style={{ margin: 0, fontSize: '1.75rem', fontWeight: 900, color: '#f8fafc' }}>
                ⚙️ Setup & Configuration Settings
              </h2>
              <p style={{ margin: '6px 0 0 0', color: '#94a3b8', fontSize: '0.92rem' }}>
                Manage college profile, principal information, contact info, and admin setup options.
              </p>
            </div>
            <button
              onClick={() => navigate('/dashboard')}
              style={{
                background: 'rgba(255, 255, 255, 0.1)',
                color: '#fff',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                padding: '10px 18px',
                borderRadius: '12px',
                fontWeight: 700,
                cursor: 'pointer',
                fontSize: '13px',
                backdropFilter: 'blur(4px)',
                transition: 'all 0.2s'
              }}
            >
              ← Back to Dashboard
            </button>
          </div>

          {/* Setup Sub-modules Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '14px',
            marginBottom: '28px'
          }}>
            <Link to="/subject-setup" style={{ textDecoration: 'none' }}>
              <div style={{
                background: '#fff', padding: '16px', borderRadius: '16px', border: '1px solid #e2e8f0',
                boxShadow: '0 4px 12px rgba(0,0,0,0.03)', display: 'flex', alignItems: 'center', gap: '12px'
              }}>
                <span style={{ fontSize: '24px' }}>📚</span>
                <div>
                  <div style={{ fontWeight: 800, color: '#0f172a', fontSize: '14px' }}>Subject Setup</div>
                  <div style={{ fontSize: '12px', color: '#64748b' }}>View master subjects</div>
                </div>
              </div>
            </Link>

            <Link to="/session-subject" style={{ textDecoration: 'none' }}>
              <div style={{
                background: '#fff', padding: '16px', borderRadius: '16px', border: '1px solid #e2e8f0',
                boxShadow: '0 4px 12px rgba(0,0,0,0.03)', display: 'flex', alignItems: 'center', gap: '12px'
              }}>
                <span style={{ fontSize: '24px' }}>📅</span>
                <div>
                  <div style={{ fontWeight: 800, color: '#0f172a', fontSize: '14px' }}>Session Subject</div>
                  <div style={{ fontSize: '12px', color: '#64748b' }}>Manage session mapping</div>
                </div>
              </div>
            </Link>

            <Link to="/teachers-database" style={{ textDecoration: 'none' }}>
              <div style={{
                background: '#fff', padding: '16px', borderRadius: '16px', border: '1px solid #e2e8f0',
                boxShadow: '0 4px 12px rgba(0,0,0,0.03)', display: 'flex', alignItems: 'center', gap: '12px'
              }}>
                <span style={{ fontSize: '24px' }}>👩‍🏫</span>
                <div>
                  <div style={{ fontWeight: 800, color: '#0f172a', fontSize: '14px' }}>Teacher Database</div>
                  <div style={{ fontSize: '12px', color: '#64748b' }}>Manage faculty records</div>
                </div>
              </div>
            </Link>

            <Link to="/class-subject" style={{ textDecoration: 'none' }}>
              <div style={{
                background: '#fff', padding: '16px', borderRadius: '16px', border: '1px solid #e2e8f0',
                boxShadow: '0 4px 12px rgba(0,0,0,0.03)', display: 'flex', alignItems: 'center', gap: '12px'
              }}>
                <span style={{ fontSize: '24px' }}>🏫</span>
                <div>
                  <div style={{ fontWeight: 800, color: '#0f172a', fontSize: '14px' }}>Class-Subject</div>
                  <div style={{ fontSize: '12px', color: '#64748b' }}>Configure class rules</div>
                </div>
              </div>
            </Link>
          </div>

          {/* Form Card */}
          <div style={{
            background: '#ffffff',
            borderRadius: '24px',
            padding: '32px',
            border: '1px solid #e2e8f0',
            boxShadow: '0 12px 30px rgba(0,0,0,0.04)'
          }}>
            <h3 style={{ margin: '0 0 20px 0', fontSize: '1.25rem', fontWeight: 800, color: '#0f172a' }}>
              🏫 College Profile & Admin Setup
            </h3>

            {loading ? (
              <div style={{ padding: '40px', textAlign: 'center', color: '#64748b', fontWeight: 600 }}>
                Loading settings data...
              </div>
            ) : (
              <form onSubmit={handleSave}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '18px', marginBottom: '24px' }}>
                  
                  {/* EIIN (Disabled) */}
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 800, color: '#64748b', marginBottom: '6px' }}>
                      College EIIN (Read-only)
                    </label>
                    <input
                      type="text"
                      value={setupData.college_eiin}
                      disabled
                      style={{
                        width: '100%', padding: '11px 14px', borderRadius: '12px', border: '1px solid #cbd5e1',
                        background: '#f1f5f9', color: '#64748b', fontWeight: 700, fontSize: '13px', cursor: 'not-allowed', boxSizing: 'border-box'
                      }}
                    />
                  </div>

                  {/* Admin Email (Disabled) */}
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 800, color: '#64748b', marginBottom: '6px' }}>
                      Admin Email (Read-only)
                    </label>
                    <input
                      type="text"
                      value={setupData.admin_email}
                      disabled
                      style={{
                        width: '100%', padding: '11px 14px', borderRadius: '12px', border: '1px solid #cbd5e1',
                        background: '#f1f5f9', color: '#64748b', fontWeight: 700, fontSize: '13px', cursor: 'not-allowed', boxSizing: 'border-box'
                      }}
                    />
                  </div>

                  {/* College Name (Bangla) */}
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 800, color: '#0f172a', marginBottom: '6px' }}>
                      College Name (Bangla) *
                    </label>
                    <input
                      type="text"
                      value={setupData.college_name}
                      onChange={e => setSetupData({ ...setupData, college_name: e.target.value })}
                      placeholder="e.g. ফেনী সাউথ-ইস্ট ডিগ্রি কলেজ"
                      required
                      style={{ width: '100%', padding: '11px 14px', borderRadius: '12px', border: '1px solid #cbd5e1', fontSize: '14px', boxSizing: 'border-box', outline: 'none' }}
                    />
                  </div>

                  {/* College Name (English) */}
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 800, color: '#0f172a', marginBottom: '6px' }}>
                      College Name (English)
                    </label>
                    <input
                      type="text"
                      value={setupData.college_name_en}
                      onChange={e => setSetupData({ ...setupData, college_name_en: e.target.value })}
                      placeholder="e.g. Feni South-East Degree College, Feni"
                      style={{ width: '100%', padding: '11px 14px', borderRadius: '12px', border: '1px solid #cbd5e1', fontSize: '14px', boxSizing: 'border-box', outline: 'none' }}
                    />
                  </div>

                  {/* Principal Name (Bangla) */}
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 800, color: '#0f172a', marginBottom: '6px' }}>
                      Principal Name (Bangla)
                    </label>
                    <input
                      type="text"
                      value={setupData.principal_name}
                      onChange={e => setSetupData({ ...setupData, principal_name: e.target.value })}
                      placeholder="e.g. অধ্যাপক মোহাম্মদ এনামুল হক খন্দকার"
                      style={{ width: '100%', padding: '11px 14px', borderRadius: '12px', border: '1px solid #cbd5e1', fontSize: '14px', boxSizing: 'border-box', outline: 'none' }}
                    />
                  </div>

                  {/* Principal Name (English) */}
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 800, color: '#0f172a', marginBottom: '6px' }}>
                      Principal Name (English)
                    </label>
                    <input
                      type="text"
                      value={setupData.principal_name_en}
                      onChange={e => setSetupData({ ...setupData, principal_name_en: e.target.value })}
                      placeholder="e.g. Professor Mohammad Enamul Hoque Khondakar"
                      style={{ width: '100%', padding: '11px 14px', borderRadius: '12px', border: '1px solid #cbd5e1', fontSize: '14px', boxSizing: 'border-box', outline: 'none' }}
                    />
                  </div>

                  {/* College Mobile */}
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 800, color: '#0f172a', marginBottom: '6px' }}>
                      College Mobile Number
                    </label>
                    <input
                      type="text"
                      value={setupData.college_mobile}
                      onChange={e => setSetupData({ ...setupData, college_mobile: e.target.value })}
                      placeholder="e.g. 01800000000"
                      style={{ width: '100%', padding: '11px 14px', borderRadius: '12px', border: '1px solid #cbd5e1', fontSize: '14px', boxSizing: 'border-box', outline: 'none' }}
                    />
                  </div>

                  {/* Contact Email */}
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 800, color: '#0f172a', marginBottom: '6px' }}>
                      College Contact Email
                    </label>
                    <input
                      type="email"
                      value={setupData.email}
                      onChange={e => setSetupData({ ...setupData, email: e.target.value })}
                      placeholder="e.g. info@college.edu.bd"
                      style={{ width: '100%', padding: '11px 14px', borderRadius: '12px', border: '1px solid #cbd5e1', fontSize: '14px', boxSizing: 'border-box', outline: 'none' }}
                    />
                  </div>

                  {/* Website URL */}
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 800, color: '#0f172a', marginBottom: '6px' }}>
                      Website URL
                    </label>
                    <input
                      type="text"
                      value={setupData.website}
                      onChange={e => setSetupData({ ...setupData, website: e.target.value })}
                      placeholder="e.g. https://www.fsedc.edu.bd"
                      style={{ width: '100%', padding: '11px 14px', borderRadius: '12px', border: '1px solid #cbd5e1', fontSize: '14px', boxSizing: 'border-box', outline: 'none' }}
                    />
                  </div>

                  {/* College Address (Bangla) */}
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 800, color: '#0f172a', marginBottom: '6px' }}>
                      College Address (Bangla)
                    </label>
                    <input
                      type="text"
                      value={setupData.college_address}
                      onChange={e => setSetupData({ ...setupData, college_address: e.target.value })}
                      placeholder="e.g. এস এস কে রোড, ফেনী"
                      style={{ width: '100%', padding: '11px 14px', borderRadius: '12px', border: '1px solid #cbd5e1', fontSize: '14px', boxSizing: 'border-box', outline: 'none' }}
                    />
                  </div>

                  {/* College Address (English) */}
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 800, color: '#0f172a', marginBottom: '6px' }}>
                      College Address (English)
                    </label>
                    <input
                      type="text"
                      value={setupData.college_address_en}
                      onChange={e => setSetupData({ ...setupData, college_address_en: e.target.value })}
                      placeholder="e.g. SSK Road, Feni, Bangladesh"
                      style={{ width: '100%', padding: '11px 14px', borderRadius: '12px', border: '1px solid #cbd5e1', fontSize: '14px', boxSizing: 'border-box', outline: 'none' }}
                    />
                  </div>

                </div>

                {msg && (
                  <div style={{
                    padding: '12px 16px', borderRadius: '12px', fontSize: '14px', fontWeight: 700, marginBottom: '20px',
                    background: msg.type === 'success' ? '#dcfce7' : '#fee2e2',
                    color: msg.type === 'success' ? '#15803d' : '#b91c1c',
                    border: `1px solid ${msg.type === 'success' ? '#bbf7d0' : '#fecaca'}`
                  }}>
                    {msg.text}
                  </div>
                )}

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                  <button
                    type="button"
                    onClick={() => navigate('/dashboard')}
                    style={{
                      padding: '11px 22px', borderRadius: '12px', border: '1px solid #cbd5e1',
                      background: '#f8fafc', color: '#475569', fontWeight: 700, cursor: 'pointer', fontSize: '14px'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    style={{
                      padding: '11px 28px', borderRadius: '12px', border: 'none',
                      background: 'linear-gradient(135deg, #ff7a00, #ff5500)', color: '#fff',
                      fontWeight: 800, cursor: 'pointer', fontSize: '14px',
                      boxShadow: '0 4px 14px rgba(255, 85, 0, 0.3)'
                    }}
                  >
                    {saving ? 'Saving Settings...' : 'Save Settings'}
                  </button>
                </div>

              </form>
            )}
          </div>

        </div>
      )}
    </PageShell>
  )
}
